import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { themeVar as colors } from "../design-tokens";
import { AppShell } from "../ui/AppShell";
import { getJobResult } from "../cma/api";
import type { AmbReportData } from "../cma/types";

/**
 * HOW THIS BRIEF'S NUMBER WAS REACHED — `/method/:jobId`.
 *
 * WHAT IT IS. The web version of the page Folio already prints. `engine/format_folio._method`
 * renders a section headed "How this was done" out of `method_lede` and `method_steps`, and
 * `engine/format_folio_view` builds those two from `data["about"]` and `data["sources"]` plus the
 * payload's own commentary on the comparable set. This screen reads the SAME payload — the object
 * `GET /api/next/amb/jobs/{id}/result` returns, which is the object the PDF and the DOCX are
 * rendered from — and lays the same material out as a page instead of a page of a document.
 *
 * IT INVENTS NOTHING, AND THAT IS THE ENTIRE POINT. Every sentence here is a string the deliverable
 * already carries. A page that explains a method has to be the method that was actually used, and
 * the only way to guarantee that is to have no second copy of it: no adjustment rules restated in
 * this file, no rounding, no re-derivation, nothing computed from a figure. ADR-0002 — React is a
 * pure renderer — and on a page whose whole subject is how a number was produced, breaking that
 * rule would not merely be a layering violation, it would make the page a liar.
 *
 * WHAT THIS FILE ACTUALLY DECIDES: the order of the sections and the words in the headings. That
 * is all, and even the order is not free — see the note above `Evidence` below.
 *
 * WHY IT NEEDS A JOB ID. There is no such thing as "the method" in the abstract: which comparables
 * qualified, which sources were read, and which disclosures apply are all facts about ONE brief.
 * A `/method` with nothing after it therefore has nothing honest to show, and says so rather than
 * printing a generic essay about comparable-sales analysis that no particular brief followed.
 *
 * GATED OFF. `flags.METHOD_PAGE_ENABLED` is `false` and the route redirects; read that constant's
 * comment for why a finished page ships dark.
 */

/** Renders a server-supplied HTML fragment as HTML, not escaped text.
 *
 * The same sink, on the same trust basis, as `cma/NarrativeReportScreen`'s `Html`: every prose
 * field in this payload is sanitised by `server/narrative_html.py` where it ENTERS the payload, to
 * an allow-list of `b i em strong br` with no attributes, so the identical inert strings reach the
 * PDF, the DOCX and this component, and the response carries an enforced CSP.
 *
 * It is redeclared here rather than imported because `NarrativeReportScreen` does not export it and
 * importing from that module would drag the build panel, the report editor and the layout picker
 * into this page for four lines. If you widen what this renders, widen the SANITISER's allow-list —
 * not this comment, and not this component.
 *
 * Escaping instead is not the safe alternative it looks like: these strings contain `<b>` and
 * `&middot;` on purpose, and printing them as text is how the review screen once showed an agent
 * "3 full / &mdash; half" while the deliverable said "3 full / — half". */
function Html({ html, style }: { html: string; style?: React.CSSProperties }) {
  return <div style={style} dangerouslySetInnerHTML={{ __html: html }} />;
}

/** The same, for a value that sits inside a row rather than owning a block. */
function HtmlSpan({ html, style }: { html: string; style?: React.CSSProperties }) {
  return <span style={style} dangerouslySetInnerHTML={{ __html: html }} />;
}

const page: React.CSSProperties = { color: colors.textPrimary };

const h2: React.CSSProperties = {
  fontSize: 17,
  fontWeight: 600,
  margin: "34px 0 10px",
  color: colors.textPrimary,
};

const prose: React.CSSProperties = {
  fontSize: 15,
  lineHeight: 1.65,
  color: colors.textPrimary,
  margin: "0 0 12px",
};

const quiet: React.CSSProperties = {
  fontSize: 13.5,
  lineHeight: 1.6,
  color: colors.textSecondary,
};

const card: React.CSSProperties = {
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  background: colors.bgSurface,
  padding: "14px 16px",
  marginBottom: 10,
};

/** A heading plus its body, omitted entirely when the payload has no body for it.
 *
 * A section that renders its heading over nothing tells the reader the brief withheld something.
 * On most briefs an absent field means the step genuinely did not happen — no subject source was
 * named, no comparable had a days-on-market figure — and the honest rendering of "this did not
 * happen" is silence, not an empty box with a title on it. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 style={h2}>{title}</h2>
      {children}
    </section>
  );
}

/** WHERE THE PROPERTY DATA CAME FROM.
 *
 * `data.sources` rows are `[label, state, what it supplied, retrieved]`, and the row set is
 * deliberately short: `amb_build._subject_source_row` emits the subject row only when the caller
 * named the source that was actually used, because the hardcoded "County record" row it replaced
 * was a false provenance tag. So a one-row table is a correct answer about a brief built from one
 * source, and this renders one row rather than implying the others are missing. */
function Sources({ rows }: { rows: AmbReportData["sources"] }) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return (
    <Section title="Where the data came from">
      <p style={{ ...quiet, margin: "0 0 12px" }}>
        Every figure in this brief traces back to one of these. Nothing was assumed from anywhere
        else.
      </p>
      {rows.map((row, i) => {
        const [label, state, supplied, retrieved] = row;
        return (
          <div key={`${label}-${i}`} style={card}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "baseline" }}>
              <span style={{ fontWeight: 600, fontSize: 14.5 }}>{label}</span>
              {state && (
                <span
                  style={{
                    fontSize: 12,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: colors.neutralChip,
                    color: colors.textSecondary,
                  }}
                >
                  {state}
                </span>
              )}
            </div>
            {supplied && <HtmlSpan html={supplied} style={{ ...quiet, display: "block", marginTop: 4 }} />}
            {retrieved && (
              <div style={{ ...quiet, marginTop: 4 }}>
                {/* The date the deliverable prints, verbatim. Formatting it here would mean picking
                    a locale and a timezone on a laptop that is not authoritative for either, and
                    the resulting page would disagree with the PDF it claims to explain. */}
                Retrieved {retrieved}
              </div>
            )}
          </div>
        );
      })}
    </Section>
  );
}

/** THE COMPARABLE SET, and what each sale contributed.
 *
 * `card.adj` is the sale's adjusted figure as the deliverable prints it — `"$317,000"`, a string,
 * not `317000.0`. It is shown beside the sale's own sentence because that sentence
 * (`card.blurb`) is what says WHY it adjusted the way it did. Neither is recomputed here; the
 * structured values live at `data.figures` and this page deliberately does not read them. */
function Evidence({ data }: { data: AmbReportData }) {
  const cards = Array.isArray(data.cards) ? data.cards : [];
  const paragraphs = Array.isArray(data.neighborhood) ? data.neighborhood : [];
  if (!cards.length && !paragraphs.length && !data.sitting) return null;

  return (
    <Section title={data.neigh_title || "The comparable set"}>
      {paragraphs.map((p, i) => (
        <Html key={i} html={p} style={prose} />
      ))}
      {cards.map((c, i) => (
        <div key={c.comp_key || `${c.name}-${i}`} style={card}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "baseline",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 14.5 }}>{c.name}</span>
            <span style={{ fontWeight: 600, fontSize: 14.5 }}>{c.adj}</span>
          </div>
          <HtmlSpan html={c.sub} style={{ ...quiet, display: "block", marginTop: 2 }} />
          {c.blurb && <Html html={c.blurb} style={{ ...quiet, marginTop: 6 }} />}
        </div>
      ))}
      {data.sitting && <Html html={data.sitting} style={{ ...quiet, marginTop: 12 }} />}
    </Section>
  );
}

/**
 * The page body. Exported on its own so a test can render it against a payload without standing up
 * a router, a session and a fetch — and so the fetching above stays the only thing this screen does
 * that can fail.
 *
 * THE SECTION ORDER IS NOT A LAYOUT PREFERENCE. Evidence runs before the figure, which is Folio's
 * spec section 5 and the reason `format_folio_view` feeds `about` (a method paragraph naming no
 * figure) into the method page while `how_reached` ("That reconciles to $336,000, which I would
 * bracket at...") is held back for the reconciliation section. A page that states the conclusion
 * above the evidence for it has answered the question it exists to ask. Keep them in this order.
 */
export function MethodBody({ data }: { data: AmbReportData }) {
  const pricing = data.pricing;
  return (
    <div style={page}>
      <h1 style={{ fontSize: 24, fontWeight: 600, margin: "0 0 6px" }}>How this was done</h1>
      <p style={{ ...quiet, margin: "0 0 4px" }}>
        {data.property?.address_line}
        {data.property?.city_state_zip ? `, ${data.property.city_state_zip}` : ""}
      </p>

      {data.about && <Html html={data.about} style={{ ...prose, marginTop: 18 }} />}

      <Sources rows={data.sources} />

      <Evidence data={data} />

      {(data.how_reached || pricing?.rationale) && (
        <Section title="How the figure was reached">
          {data.how_reached && <Html html={data.how_reached} style={prose} />}
          {pricing?.rationale && <Html html={pricing.rationale} style={prose} />}
        </Section>
      )}

      {(data.disclaimer_535 || data.notappr || data.limits) && (
        <Section title="What this brief is, and what it is not">
          {/* All three, in the order the deliverable prints them, and all three every time. These
              are the paragraphs that say this is not an appraisal; choosing which of them a reader
              sees is not a presentation decision. `notappr` carries the tier disclosure exactly
              once and `limits` deliberately does not repeat it — see amb_build's note on the
              duplicate-paragraph defect. */}
          {data.disclaimer_535 && <Html html={data.disclaimer_535} style={quiet} />}
          {data.notappr && <Html html={data.notappr} style={{ ...quiet, marginTop: 10 }} />}
          {data.limits && <Html html={data.limits} style={{ ...quiet, marginTop: 10 }} />}
        </Section>
      )}
    </div>
  );
}

export function MethodScreen() {
  const { jobId } = useParams<{ jobId: string }>();
  const [data, setData] = useState<AmbReportData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    setData(null);
    setError(null);
    getJobResult(jobId)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Could not load this brief.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  // NAVIGATION rule 6, no dead-ends. Each of these three states says what happened and offers the
  // one action that gets the reader somewhere, rather than leaving a blank column.
  let body: React.ReactNode;
  if (!jobId) {
    body = (
      <div style={page}>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: "0 0 8px" }}>How a brief is built</h1>
        <p style={prose}>
          This page explains how one brief reached its number — which sales it used, what they were
          adjusted for, and what the figure does and does not claim. It needs a brief to explain, so
          open one and come back to it from there.
        </p>
        <p style={prose}>
          <a href="/cma" style={{ color: colors.accent }}>
            Start a new analysis
          </a>
        </p>
      </div>
    );
  } else if (error) {
    body = (
      <div style={page}>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: "0 0 8px" }}>How this was done</h1>
        <p style={prose}>{error}</p>
        <p style={quiet}>
          {/* Deliberately not "this brief does not exist". A brief that is still building, or that
              belongs to another account, fails here the same way, and telling a reader a record is
              gone when it may simply not be theirs is both wrong and an information leak. */}
          A brief has to have finished building before its method can be shown.
        </p>
      </div>
    );
  } else if (!data) {
    body = <p style={quiet}>Loading this brief…</p>;
  } else {
    body = <MethodBody data={data} />;
  }

  return <AppShell step="method">{body}</AppShell>;
}

export default MethodScreen;
